import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["traitsList", "selectedTraits", "traitsRemaining", "heritageTypeSelection", "heritageTypeInput", "naturesBlessingSelections", "form"]

  static values = {
    heritage: String,
    maxTraits: Number,
    selectedTraits: Array,
    selectedType: String
  }

  connect() {
    this.heritage = this.heritageValue
    this.selectedTraitsValue = this.normalizeSelectedTraits(this.selectedTraitsValue)
    this.selectedTypeValue = this.normalizeSelectedType(this.selectedTypeValue)
    
    this.naturesBlessingSelections = this.loadNaturesBlessingSelections()
    this.syncHiddenState()
    
    if (this.heritage === 'Any') {
      this.renderAnyHeritageTypeSelection()
      if (this.selectedTypeValue) {
        this.loadTraitsForHeritage()
      }
    } else if (this.heritage === 'Green/Blighted/Uplifted') {
      this.renderHeritageTypeSelection()
      if (this.selectedTypeValue) {
        this.loadTraitsForHeritage()
      }
    } else {
      this.loadTraitsForHeritage()
    }
    this.updateTraitsRemaining()
  }

  normalizeSelectedTraits(traits) {
    if (!Array.isArray(traits)) return []

    const normalizedTraits = traits
      .map(trait => typeof trait === 'string' ? trait.trim() : '')
      .filter(Boolean)

    return [...new Set(normalizedTraits)]
  }

  normalizeSelectedType(type) {
    return typeof type === 'string' ? type.trim() : ''
  }

  loadNaturesBlessingSelections() {
    if (!this.hasNaturesBlessingSelectionsTarget) return {}

    try {
      const selectionsJson = this.naturesBlessingSelectionsTarget.value
      const parsed = selectionsJson ? JSON.parse(selectionsJson) : {}
      return this.normalizeNaturesBlessingSelections(parsed)
    } catch (e) {
      return {}
    }
  }

  normalizeNaturesBlessingSelections(selections) {
    if (!selections || typeof selections !== 'object') return {}

    const primary = typeof selections.primary === 'string' ? selections.primary.trim() : ''
    const secondary = typeof selections.secondary === 'string' ? selections.secondary.trim() : ''
    const normalized = {}

    if (primary) normalized.primary = primary
    if (secondary && secondary !== primary) normalized.secondary = secondary

    return normalized
  }

  currentHeritageType() {
    const needsTypeSelection = this.heritage === 'Green/Blighted/Uplifted' || this.heritage === 'Any'
    return needsTypeSelection ? this.selectedTypeValue : this.heritage
  }

  currentHeritageData() {
    return this.getTraitsForHeritage(this.currentHeritageType())
  }

  syncHiddenState() {
    if (this.hasHeritageTypeInputTarget) {
      this.heritageTypeInputTarget.value = this.selectedTypeValue
    }

    if (this.hasNaturesBlessingSelectionsTarget) {
      this.naturesBlessingSelectionsTarget.value = JSON.stringify(this.naturesBlessingSelections || {})
    }
  }

  renderHeritageTypeSelection() {
    // Determine which radio button should be checked
    let checkedGreen = ''
    let checkedBlighted = ''
    let checkedUplifted = ''
    
    if (this.selectedTypeValue === 'Green') {
      checkedGreen = 'checked'
    } else if (this.selectedTypeValue === 'Blighted') {
      checkedBlighted = 'checked'
    } else if (this.selectedTypeValue === 'Uplifted' || this.selectedTypeValue.startsWith('Uplifted ')) {
      checkedUplifted = 'checked'
    }
    
    this.traitsListTarget.innerHTML = `
      <div class="mb-6">
        <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-4">Select Heritage Type</h3>
        <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <label for="heritage_type_green" class="flex cursor-pointer items-start gap-3 rounded-2xl border border-gray-200 bg-gray-50/80 p-4 transition-colors hover:border-blue-300 hover:bg-blue-50 dark:border-gray-700 dark:bg-gray-900/30 dark:hover:border-blue-500 dark:hover:bg-blue-900/20">
            <input type="radio" 
                   name="character[heritage_type]" 
                   value="Green" 
                   id="heritage_type_green"
                   class="mt-1 h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600"
                   data-action="heritage-traits#selectHeritageType"
                   ${checkedGreen}>
            <span>
              <span class="block font-medium text-gray-900 dark:text-white">People of the Green</span>
              <span class="mt-1 block text-sm text-gray-500 dark:text-gray-400">Mystical inheritances and bargains with the Green.</span>
            </span>
          </label>
          <label for="heritage_type_blighted" class="flex cursor-pointer items-start gap-3 rounded-2xl border border-gray-200 bg-gray-50/80 p-4 transition-colors hover:border-blue-300 hover:bg-blue-50 dark:border-gray-700 dark:bg-gray-900/30 dark:hover:border-blue-500 dark:hover:bg-blue-900/20">
            <input type="radio" 
                   name="character[heritage_type]" 
                   value="Blighted" 
                   id="heritage_type_blighted"
                   class="mt-1 h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600"
                   data-action="heritage-traits#selectHeritageType"
                   ${checkedBlighted}>
            <span>
              <span class="block font-medium text-gray-900 dark:text-white">The Blighted</span>
              <span class="mt-1 block text-sm text-gray-500 dark:text-gray-400">Mutated forms, extra limbs, and harsh survivability.</span>
            </span>
          </label>
          <label for="heritage_type_uplifted" class="flex cursor-pointer items-start gap-3 rounded-2xl border border-gray-200 bg-gray-50/80 p-4 transition-colors hover:border-blue-300 hover:bg-blue-50 dark:border-gray-700 dark:bg-gray-900/30 dark:hover:border-blue-500 dark:hover:bg-blue-900/20">
            <input type="radio" 
                   name="character[heritage_type]" 
                   value="Uplifted" 
                   id="heritage_type_uplifted"
                   class="mt-1 h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600"
                   data-action="heritage-traits#selectHeritageType"
                   ${checkedUplifted}>
            <span>
              <span class="block font-medium text-gray-900 dark:text-white">Uplifted Animals</span>
              <span class="mt-1 block text-sm text-gray-500 dark:text-gray-400">A single species package that defines the whole body.</span>
            </span>
          </label>
        </div>
      </div>
      <div data-heritage-traits-target="heritageTypeSelection"></div>
    `
  }

  renderAnyHeritageTypeSelection() {
    // For priority A/B, allow any heritage type
    const types = ['Human', 'Green', 'Blighted', 'Uplifted', 'Synthetic']
    const labels = {
      'Human': 'Human',
      'Green': 'People of the Green',
      'Blighted': 'The Blighted',
      'Uplifted': 'Uplifted Animals',
      'Synthetic': 'Synthetic'
    }

    const radioButtons = types.map(type => {
      const checked = this.selectedTypeValue === type ||
        (type === 'Uplifted' && this.selectedTypeValue.startsWith('Uplifted '))
        ? 'checked' : ''
      return `
        <label for="heritage_type_${type.toLowerCase()}" class="flex cursor-pointer items-start gap-3 rounded-2xl border border-gray-200 bg-gray-50/80 p-4 transition-colors hover:border-blue-300 hover:bg-blue-50 dark:border-gray-700 dark:bg-gray-900/30 dark:hover:border-blue-500 dark:hover:bg-blue-900/20">
          <input type="radio"
                 name="character[heritage_type]"
                 value="${type}"
                 id="heritage_type_${type.toLowerCase()}"
                 class="mt-1 h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600"
                 data-action="heritage-traits#selectHeritageType"
                 ${checked}>
          <span class="font-medium text-gray-900 dark:text-white">
            ${labels[type]}
          </span>
        </label>
      `
    }).join('')

    this.traitsListTarget.innerHTML = `
      <div class="mb-6">
        <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-4">Select Heritage Type</h3>
        <p class="text-sm text-gray-600 dark:text-gray-400 mb-4">
          With Heritage Priority A or B, you may select any heritage type.
        </p>
        <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
          ${radioButtons}
        </div>
      </div>
      <div data-heritage-traits-target="heritageTypeSelection"></div>
    `
  }

  selectHeritageType(event) {
    this.selectedTypeValue = event.target.value
    this.selectedTraitsValue = []
    this.naturesBlessingSelections = {}
    this.syncHiddenState()
    this.loadTraitsForHeritage()
    this.updateTraitsRemaining()
    this.updateSelectedTraits()
  }

  loadTraitsForHeritage() {
    const needsTypeSelection = this.heritage === 'Green/Blighted/Uplifted' || this.heritage === 'Any'
    const traits = this.currentHeritageData()

    if (needsTypeSelection) {
      this.heritageTypeSelectionTarget.innerHTML = this.renderTraitsList(traits)
    } else {
      this.traitsListTarget.innerHTML = this.renderTraitsList(traits)
    }

    this.updateSelectedTraits()
    this.updateTraitsRemaining()
  }

  getTraitsForHeritage(heritage) {
    switch(heritage) {
      case 'Green':
        return {
          type: 'Green',
          baseTraits: 1,
          maxTraits: 2,
          traits: [
            { name: 'Chimerical', description: '+1 die limit on all sorcery, conjuring, channeling, and astral senses tests' },
            { name: 'Enchanting', description: '+3 Charisma, can cast Enthrall at a force level up to Zoetic Potential, even without magical talent' },
            { name: 'Nature\'s Blessing', description: 'Increase one stat and its maximum by 3, and another stat by 1' },
            { name: 'Redcap', description: '+2 Strength and Body (and their maximums), +2 free dice on all soak tests, but must consume blood and flesh monthly' },
            { name: 'Shapechanging', description: 'Can shift into an animal form, substituting its physical traits' },
            { name: 'Resmedis', description: 'Can make an object disappear for up to an hour' },
            { name: 'Otherworldly', description: 'Impossible to determine your age; spirits treat their force as one lower against you' },
            { name: 'Astral Flame', description: 'Can illuminate a target with harmless flame for 10 minutes' },
            { name: 'Wildling', description: 'Can transform into a beast-like form, gaining physical bonuses at the cost of mental penalties' },
            { name: 'Wind Walk', description: 'Increases movement speed by 4 meters (2")' }
          ],
          drawbacks: [
            { name: 'Allergies', description: 'Contact with iron causes damage' },
            { name: 'Antlers', description: 'Cannot wear helmets or headgear' },
            { name: 'Compulsive', description: 'Must perform certain actions habitually or suffer penalties' },
            { name: 'Green Skin', description: 'Skin is green and hair changes with the seasons' },
            { name: 'Nature Bound', description: 'Cannot cross running water, takes penalties in bright light, must be invited into personal spaces' },
            { name: 'You Are Smol', description: 'Character is between 2–4 feet in height' }
          ]
        }
      case 'Blighted':
        return {
          type: 'Blighted',
          baseTraits: 2,
          maxTraits: 3,
          traits: [
            { name: 'Analgesia', description: 'Immune to pain; the GM tracks wounds' },
            { name: 'Camouflage', description: 'Gain a +2 bonus to hiding tests; can blend into surroundings' },
            { name: 'Extra Arm', description: 'Gain an extra arm (+4 Reaction, but armor and gear cost 50% more)' },
            { name: 'Extra Leg', description: 'Gain a third leg, increasing base movement by 2 meters' },
            { name: 'Hephaestus', description: 'Gain +2 free dice on Engineering tests' },
            { name: 'Huge', description: '9–12 feet tall, +4 Strength and Body (and their maximums)' },
            { name: 'Immortal', description: 'No aging, immune to cancer, but continue to grow slowly over time' },
            { name: 'Tough', description: 'Chitinous skin grants natural armor (Ballistic & Impact 2)' },
            { name: 'Unstoppable', description: 'Always reroll 1s when soaking damage' }
          ],
          drawbacks: [
            { name: 'Animal Head', description: 'Head resembles a bovine, pig, wolf, or bird' },
            { name: 'Bulky', description: 'Huge size slows movement by 2 meters (1")' }
          ]
        }
      case 'Uplifted':
        return {
          type: 'Uplifted',
          baseTraits: 1,
          maxTraits: 1,
          traits: [
            { name: 'Uplifted Dog', description: '+2 Reaction, -1 max Willpower, +2 bonus dice to Observation tests, can move on all fours 10m (5")', penalty: -1 },
            { name: 'Uplifted Bear', description: '+2 Strength, +4 Body, +2 bonus dice to all soak tests', penalty: 0 },
            { name: 'Uplifted Gorilla', description: '+4 Strength, +2 Body, natural reach of 1', penalty: 0 },
            { name: 'Uplifted Octopus', description: '+2 arms, +2 Intelligence, gains Camouflage trait', penalty: 0 },
            { name: 'Uplifted Shark', description: '+4 Body (max), melee bite attack (reach 0, Damage 6+Str)', penalty: 0 },
            { name: 'Uplifted Dolphin', description: '+6 Intelligence (max), can hold breath for Body attribute minutes', penalty: 0 },
            { name: 'Uplifted Snake', description: '+4 Strength (max), +2 Body (max), +2 Impact armor, venomous bite or spit attack', penalty: -2 },
            { name: 'Uplifted Rabbit', description: '+4 Reaction (max), -8 max Strength, -8 max Body, acts first before initiative, can move on all fours 12m (6")', penalty: -4 },
            { name: 'Uplifted Cat', description: '+2 Reaction (max), +1 Charisma (max), +2 bonus dice on Shadow tests, can move on all fours 10m (5")', penalty: 0 },
            { name: 'Uplifted Raccoon', description: '+2 Reaction (max), -6 max Strength, -8 max Body, can mount creatures for cover, +2 bonus dice on dodge tests', penalty: -4 },
            { name: 'Uplifted Chameleon', description: '+1 bonus die on Shadow tests, gains Camouflage trait, can climb walls, -4 max Strength, -4 max Body', penalty: -4 },
            { name: 'Uplifted Rat', description: '+4 Reaction (max), -6 max Strength, -4 max Body, +1 bonus die to all tests', penalty: -4 },
            { name: 'Uplifted Raven', description: '+2 Reaction (max), -6 max Strength, -8 max Body, flight 12m (6"), natural vision magnification 2', penalty: -4 },
            { name: 'Uplifted Bat', description: '+2 Reaction (max), -6 max Strength, -8 max Body, flight 8m (4"), echolocation awareness within 24m (12")', penalty: -4 }
          ]
        }
      case 'Synthetic':
        return {
          type: 'Synthetic',
          baseTraits: 0, // No free traits - Synthetics can select any number of frame modifications
          maxTraits: 5, // Can select any number of their traits (all 5)
          inherentTraits: [
            { name: 'Durable', description: 'Synthetics ignore 2 points of damage from each attack. This trait is lost if Specialization or Arcano-Manon Interface Matrix is selected.' }
          ],
          traits: [
            { name: 'No Head', description: 'Sensory organs distributed throughout frame. Gain +1 weight weapon mount.' },
            { name: 'Polypedal Legs', description: 'Instead of human-like legs, choose 4, 6, or 8 legs. Each extra pair reduces movement by -1m (-5") and gives +2 bonus dice on athletics tests.' },
            { name: 'Heavy Torso', description: 'Provides 2 additional +1 weight mounts for extra arms or weapons. Bulky torso reduces movement by 2m (-1").' },
            { name: 'Specialization', description: 'Removes Durable trait. Gain +1 bonus die to all tests from a single pool of your choice.' },
            { name: 'Arcano-Manon Interface Matrix', description: 'Removes Durable trait. Treat your Zoetic Potential as 3 ranks higher than actual rank for magic use (effective ZP of 4).' }
          ]
        }
      case 'Human':
        return {
          type: 'Human',
          baseTraits: 0,
          maxTraits: 0,
          traits: [],
          drawbacks: []
        }
      default:
        return { traits: [], drawbacks: [], baseTraits: 0, maxTraits: 0, type: null }
    }
  }

  renderTraitsList(traitData) {
    let html = ''
    
    // Add description based on heritage type
    switch(traitData.type) {
      case 'Green':
        html += `<p class="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Choose one heritage trait for free. You may take a second trait if you also select a Green Drawback.
        </p>`
        break
      case 'Blighted':
        html += `<p class="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Select two free heritage traits. You may select a drawback to gain a third trait.
        </p>`
        break
      case 'Uplifted':
        html += `<p class="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Select one species trait that defines your uplifted animal type.
        </p>`
        break
      case 'Synthetic':
        html += `<p class="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Synthetic characters start with the <strong>Durable</strong> trait (ignore 2 damage from each attack).
          You may select any number of frame modifications. Note: Selecting <em>Specialization</em> or
          <em>Arcano-Manon Interface Matrix</em> removes the Durable trait.
        </p>`
        break
      case 'Human':
        html += `<p class="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Humans have no special heritage traits. You may proceed to the next step.
        </p>`
        break
    }

    // Add traits
    if (traitData.traits && traitData.traits.length > 0) {
    html += `<div class="grid grid-cols-1 gap-3 lg:grid-cols-2">
      ${traitData.traits.map(trait => this.renderTraitOption(trait)).join('')}
    </div>`
    }

    // Add drawbacks if they exist
    if (traitData.drawbacks && traitData.drawbacks.length > 0) {
      html += `
        <h3 class="text-lg font-medium text-gray-900 dark:text-white mt-6 mb-4">Drawbacks</h3>
        <div class="grid grid-cols-1 gap-3 lg:grid-cols-2">
          ${traitData.drawbacks.map(drawback => this.renderTraitOption(drawback, true)).join('')}
        </div>
      `
    }

    return html
  }

  renderTraitOption(trait, isDrawback = false) {
    const id = `trait_${trait.name.toLowerCase().replace(/\s+/g, '_')}`
    const penaltyText = trait.penalty !== undefined ? ` (Attribute Penalty: ${trait.penalty})` : ''

    // Check if trait is selected (handle Polypedal Legs variants)
    let isChecked = this.selectedTraitsValue.includes(trait.name)
    if (trait.name === 'Polypedal Legs') {
      isChecked = this.selectedTraitsValue.some(t => t.startsWith('Polypedal Legs'))
    }

    return `
      <div class="relative rounded-2xl border ${isDrawback ? 'drawback border-red-200 bg-red-50/70 dark:border-red-800/60 dark:bg-red-900/20' : 'border-gray-200 bg-gray-50/80 dark:border-gray-700 dark:bg-gray-900/30'} p-4">
        <input type="checkbox"
               name="character[heritage_traits][]"
               value="${trait.name}"
               id="${id}"
               class="absolute left-4 top-4 h-4 w-4"
               data-action="heritage-traits#toggleTrait"
               ${isChecked ? 'checked' : ''}>
        <label for="${id}" class="block cursor-pointer pl-7">
          <div class="font-medium text-gray-900 dark:text-white">${trait.name}${penaltyText}</div>
          <div class="mt-1 text-sm text-gray-500 dark:text-gray-400">${trait.description}</div>
        </label>
      </div>
    `
  }

  toggleTrait(event) {
    const traitName = event.target.value
    const isDrawback = event.target.closest('.drawback') !== null
    const heritageData = this.currentHeritageData()

    if (!heritageData) return // Exit if no heritage data is available

    if (event.target.checked) {
      // Handle trait selection
      if (isDrawback) {
        this.selectedTraitsValue = [...this.selectedTraitsValue, `Drawback: ${traitName}`]
      } else {
        const currentTraits = this.selectedTraitsValue.filter(t => !t.startsWith('Drawback:'))
        const currentDrawbacks = this.selectedTraitsValue.filter(t => t.startsWith('Drawback:'))

        // Calculate allowed traits based on heritage type
        let maxAllowedTraits = heritageData.maxTraits || heritageData.baseTraits
        if (heritageData.type === 'Green') {
          // Green can get extra traits from drawbacks
          maxAllowedTraits = heritageData.baseTraits + currentDrawbacks.length
        } else if (heritageData.type === 'Blighted' && currentDrawbacks.length > 0) {
          // Blighted can get one extra trait from drawbacks
          maxAllowedTraits = heritageData.baseTraits + 1
        }
        // Synthetics and others use maxTraits directly (e.g., 5 for Synthetics)

        // Prevent selection if exceeding limits
        if (currentTraits.length >= maxAllowedTraits) {
          event.preventDefault()
          event.target.checked = false
          return
        }

        this.selectedTraitsValue = [...this.selectedTraitsValue, traitName]
        
        // Update heritage_type based on the heritage type
        if (heritageData.type === 'Uplifted' && traitName.startsWith('Uplifted ')) {
          // For uplifted animals, update heritage_type to the specific animal
          this.selectedTypeValue = traitName
          this.syncHiddenState()
        } else if (heritageData.type !== 'Uplifted') {
          // For Green, Blighted, and other types, ensure heritage_type matches the type
          this.selectedTypeValue = heritageData.type
          this.syncHiddenState()
        }
      }
    } else {
      // Handle trait deselection
      if (isDrawback) {
        // Remove the drawback and any excess traits
        const drawbackName = `Drawback: ${traitName}`
        const currentTraits = this.selectedTraitsValue.filter(t => !t.startsWith('Drawback:'))
        const remainingDrawbacks = this.selectedTraitsValue
          .filter(t => t.startsWith('Drawback:') && t !== drawbackName)
        
        // Recalculate allowed traits
        let allowedTraits = heritageData.baseTraits
        if (heritageData.type === 'Green') {
          allowedTraits += remainingDrawbacks.length
        } else if (heritageData.type === 'Blighted' && remainingDrawbacks.length > 0) {
          allowedTraits += 1
        }
        
        // Keep only allowed number of traits
        const validTraits = currentTraits.slice(0, allowedTraits)
        this.selectedTraitsValue = [...validTraits, ...remainingDrawbacks]
      } else {
        this.selectedTraitsValue = this.selectedTraitsValue.filter(t => t !== traitName)
        
        // Clear Nature's Blessing selections if the trait is removed
        if (traitName === 'Nature\'s Blessing') {
          this.naturesBlessingSelections = {}
          if (this.hasNaturesBlessingSelectionsTarget) {
            this.naturesBlessingSelectionsTarget.value = JSON.stringify({})
          }
        }
        
        // Update heritage_type based on remaining traits
        if (heritageData.type === 'Uplifted' && traitName.startsWith('Uplifted ')) {
          // For uplifted animals, reset heritage_type to "Uplifted" if no specific animal is selected
          const hasOtherUpliftedAnimals = this.selectedTraitsValue.some(t => t.startsWith('Uplifted '))
          if (!hasOtherUpliftedAnimals) {
            this.selectedTypeValue = 'Uplifted'
            this.syncHiddenState()
          }
        } else if (heritageData.type !== 'Uplifted') {
          // For Green, Blighted, and other types, check if any traits remain
          const hasTraitsOfThisType = this.selectedTraitsValue.some(t => {
            // Check if this trait belongs to the current heritage type
            const traitData = this.findTraitData(t)
            return traitData && traitData.type === heritageData.type
          })
          
          if (!hasTraitsOfThisType) {
            // No traits of this type remain, reset to the base type
            this.selectedTypeValue = heritageData.type
            this.syncHiddenState()
          }
        }
      }
    }

    this.updateSelectedTraits()
    this.updateTraitsRemaining()
  }

  updateSelectedTraits() {
    const heritageData = this.currentHeritageData()

    // Handle inherent traits (e.g., Durable for Synthetics)
    let inherentTraitsHtml = ''
    if (heritageData.inherentTraits && heritageData.inherentTraits.length > 0) {
      // Check if any selected traits remove inherent traits
      const removesInherent = this.selectedTraitsValue.some(t =>
        t === 'Specialization' || t === 'Arcano-Manon Interface Matrix'
      )

      inherentTraitsHtml = heritageData.inherentTraits.map(trait => {
        const isRemoved = removesInherent
        return `
          <div class="flex items-start space-x-3 p-2 rounded ${isRemoved ? 'bg-red-50 dark:bg-red-900/20 line-through opacity-60' : 'bg-green-50 dark:bg-green-900/20'}">
            <div class="flex-1">
              <div class="font-medium ${isRemoved ? 'text-red-700 dark:text-red-400' : 'text-green-700 dark:text-green-400'}">
                ${trait.name} ${isRemoved ? '(Removed)' : '(Inherent)'}
              </div>
              <div class="text-sm text-gray-500 dark:text-gray-400">${trait.description}</div>
            </div>
          </div>
        `
      }).join('')
    }

    const traitsList = this.selectedTraitsValue
      .filter(trait => !trait.startsWith('Drawback:'))
      .map(trait => {
        const traitData = this.findTraitData(trait)
        return this.renderSelectedTrait(trait, false, traitData?.description)
      })

    const drawbacksList = this.selectedTraitsValue
      .filter(trait => trait.startsWith('Drawback:'))
      .map(trait => {
        const drawbackName = trait.substring(9)
        const drawbackData = this.findTraitData(drawbackName, true)
        return this.renderSelectedTrait(drawbackName, true, drawbackData?.description)
      })

    // Calculate and display stat modifications
    const statMods = this.calculateStatModifications()
    const statModsHtml = Object.entries(statMods)
      .filter(([_, value]) => value !== 0)
      .map(([stat, value]) => `
        <div class="text-sm text-gray-600 dark:text-gray-400">
          ${stat}: ${value > 0 ? '+' : ''}${value}
        </div>
      `).join('')

    this.selectedTraitsTarget.innerHTML = `
      ${inherentTraitsHtml ? '<h3 class="text-lg font-medium text-gray-900 dark:text-white mb-2">Inherent Traits</h3>' : ''}
      ${inherentTraitsHtml}
      ${traitsList.length > 0 ? '<h3 class="text-lg font-medium text-gray-900 dark:text-white mt-4 mb-2">Selected Traits</h3>' : ''}
      ${traitsList.join('')}
      ${drawbacksList.length > 0 ? '<h3 class="text-lg font-medium text-gray-900 dark:text-white mt-4 mb-2">Selected Drawbacks</h3>' : ''}
      ${drawbacksList.join('')}
      ${statModsHtml ? `
        <h3 class="text-lg font-medium text-gray-900 dark:text-white mt-4 mb-2">Stat Modifications</h3>
        <div class="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
          ${statModsHtml}
        </div>
      ` : ''}
    `
    
    // Re-attach event listeners for Nature's Blessing select dropdowns
    this.attachNaturesBlessingListeners()
  }

  attachNaturesBlessingListeners() {
    // Find all Nature's Blessing selects and attach listeners if not already attached
    const selects = this.element.querySelectorAll('[data-action*="updateNaturesBlessingSelections"]')
    selects.forEach(select => {
      if (!select.dataset.listenerAttached) {
        select.dataset.listenerAttached = 'true'
        // Listener is already attached via data-action, but we need to update secondary options
        // when primary changes
        if (select.dataset.attributeType === 'primary') {
          select.addEventListener('change', () => {
            this.updateSecondaryOptions()
          })
        }
      }
    })
  }

  updateSecondaryOptions() {
    const primary = this.naturesBlessingSelections?.primary || ''
    const secondarySelect = this.element.querySelector('[data-attribute-type="secondary"]')
    
    if (secondarySelect) {
      const currentSecondary = secondarySelect.value
      const attributes = ['Strength', 'Body', 'Reaction', 'Intelligence', 'Willpower', 'Charisma']
      
      // Update options
      secondarySelect.innerHTML = `
        <option value="">Select...</option>
        ${attributes.filter(attr => attr !== primary).map(attr => `
          <option value="${attr}" ${currentSecondary === attr && attr !== primary ? 'selected' : ''}>${attr}</option>
        `).join('')}
      `
      
      // Clear secondary if it matches primary
      if (currentSecondary === primary) {
        secondarySelect.value = ''
        this.naturesBlessingSelections.secondary = ''
        if (this.hasNaturesBlessingSelectionsTarget) {
          this.naturesBlessingSelectionsTarget.value = JSON.stringify(this.naturesBlessingSelections)
        }
      }
    }
  }

  findTraitData(traitName, isDrawback = false) {
    const heritageData = this.currentHeritageData()

    if (!heritageData) return null

    const list = isDrawback ? heritageData.drawbacks : heritageData.traits

    // Handle Polypedal Legs variants (e.g., "Polypedal Legs (6 legs)" -> match "Polypedal Legs")
    if (traitName.startsWith('Polypedal Legs')) {
      return list?.find(t => t.name === 'Polypedal Legs')
    }

    return list?.find(t => t.name === traitName)
  }

  calculateStatModifications() {
    const mods = {
      Strength: 0,
      Body: 0,
      Reaction: 0,
      Intelligence: 0,
      Willpower: 0,
      Charisma: 0
    }

    this.selectedTraitsValue.forEach(trait => {
      const isDrawback = trait.startsWith('Drawback:')
      const traitName = isDrawback ? trait.substring(9) : trait
      const traitData = this.findTraitData(traitName, isDrawback)

      if (!traitData) return

      switch(traitName) {
        case 'Enchanting':
          mods.Charisma += 3
          break
        case 'Redcap':
          mods.Strength += 2
          mods.Body += 2
          break
        case 'Nature\'s Blessing':
          // Apply Nature's Blessing modifications
          if (this.naturesBlessingSelections) {
            const primary = this.naturesBlessingSelections.primary
            const secondary = this.naturesBlessingSelections.secondary
            if (primary && mods.hasOwnProperty(primary)) {
              mods[primary] += 3
            }
            if (secondary && mods.hasOwnProperty(secondary)) {
              mods[secondary] += 1
            }
          }
          break
        case 'Extra Arm':
          mods.Reaction += 4
          break
        case 'Huge':
          mods.Strength += 4
          mods.Body += 4
          break
        case 'Uplifted Rabbit':
          mods.Reaction += 4
          break
        case 'Uplifted Cat':
          mods.Reaction += 2
          mods.Charisma += 1
          break
        case 'Uplifted Raccoon':
          mods.Reaction += 2
          break
        case 'Uplifted Rat':
          mods.Reaction += 4
          break
      }
    })

    return mods
  }

  renderSelectedTrait(trait, isDrawback, description) {
    // Special handling for Nature's Blessing - show ability selection UI
    if (trait === 'Nature\'s Blessing' && !isDrawback) {
      return this.renderNaturesBlessingTrait(trait, description)
    }

    // Special handling for Polypedal Legs - show leg count selection UI
    if (trait.startsWith('Polypedal Legs') && !isDrawback) {
      return this.renderPolypedalLegsTrait(trait, description)
    }
    
    return `
      <div class="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 space-y-1">
        <div class="flex justify-between items-start">
          <div class="font-medium text-gray-900 dark:text-white">
            ${isDrawback ? '⚠️ ' : ''}${trait}
          </div>
          <button type="button" 
                  class="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 ml-2"
                  data-action="click->heritage-traits#removeTrait"
                  data-trait="${isDrawback ? 'Drawback: ' : ''}${trait}">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>
        ${description ? `
          <div class="text-sm text-gray-600 dark:text-gray-400">
            ${description}
          </div>
        ` : ''}
      </div>
    `
  }

  renderNaturesBlessingTrait(trait, description) {
    const attributes = ['Strength', 'Body', 'Reaction', 'Intelligence', 'Willpower', 'Charisma']
    const primary = this.naturesBlessingSelections?.primary || ''
    const secondary = this.naturesBlessingSelections?.secondary || ''
    
    return `
      <div class="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 space-y-3">
        <div class="flex justify-between items-start">
          <div class="font-medium text-gray-900 dark:text-white">
            ${trait}
          </div>
          <button type="button" 
                  class="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 ml-2"
                  data-action="click->heritage-traits#removeTrait"
                  data-trait="${trait}">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>
        ${description ? `
          <div class="text-sm text-gray-600 dark:text-gray-400">
            ${description}
          </div>
        ` : ''}
        <div class="space-y-2 mt-3">
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Primary Attribute (+3, max +3):
            </label>
            <select class="w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm text-gray-900 dark:text-white focus:ring-blue-500 focus:border-blue-500"
                    data-action="change->heritage-traits#updateNaturesBlessingSelections"
                    data-attribute-type="primary">
              <option value="">Select...</option>
              ${attributes.map(attr => `
                <option value="${attr}" ${primary === attr ? 'selected' : ''}>${attr}</option>
              `).join('')}
            </select>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Secondary Attribute (+1):
            </label>
            <select class="w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm text-gray-900 dark:text-white focus:ring-blue-500 focus:border-blue-500"
                    data-action="change->heritage-traits#updateNaturesBlessingSelections"
                    data-attribute-type="secondary">
              <option value="">Select...</option>
              ${attributes.filter(attr => attr !== primary).map(attr => `
                <option value="${attr}" ${secondary === attr ? 'selected' : ''}>${attr}</option>
              `).join('')}
            </select>
          </div>
        </div>
      </div>
    `
  }

  renderPolypedalLegsTrait(trait, description) {
    // Extract current leg count from trait name (e.g., "Polypedal Legs (6 legs)" -> 6)
    const match = trait.match(/\((\d+) legs?\)/)
    const currentLegCount = match ? parseInt(match[1]) : ''

    const legOptions = [4, 6, 8]

    return `
      <div class="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 space-y-3">
        <div class="flex justify-between items-start">
          <div class="font-medium text-gray-900 dark:text-white">
            Polypedal Legs
          </div>
          <button type="button"
                  class="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 ml-2"
                  data-action="click->heritage-traits#removeTrait"
                  data-trait="${trait}">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>
        <div class="text-sm text-gray-600 dark:text-gray-400">
          Choose 4, 6, or 8 legs. Each extra pair reduces movement by -1m and gives +2 bonus dice on athletics tests.
        </div>
        <div class="mt-3">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Number of Legs:
          </label>
          <select class="w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm text-gray-900 dark:text-white focus:ring-blue-500 focus:border-blue-500"
                  data-action="change->heritage-traits#updatePolypedalLegsCount"
                  data-current-trait="${trait}">
            <option value="">Select...</option>
            ${legOptions.map(count => `
              <option value="${count}" ${currentLegCount === count ? 'selected' : ''}>
                ${count} legs (movement -${(count - 2) / 2}m, +${count - 2} athletics dice)
              </option>
            `).join('')}
          </select>
        </div>
        ${currentLegCount ? `
          <div class="text-sm text-blue-600 dark:text-blue-400 mt-2">
            <strong>Effects:</strong> Movement -${(currentLegCount - 2) / 2}m, Athletics +${currentLegCount - 2} dice
          </div>
        ` : `
          <div class="text-sm text-amber-600 dark:text-amber-400 mt-2">
            ⚠️ Please select number of legs
          </div>
        `}
      </div>
    `
  }

  updatePolypedalLegsCount(event) {
    const newLegCount = event.target.value
    const currentTrait = event.target.dataset.currentTrait

    if (!newLegCount) return

    // Remove the old Polypedal Legs trait (any variant)
    this.selectedTraitsValue = this.selectedTraitsValue.filter(t => !t.startsWith('Polypedal Legs'))

    // Add the new Polypedal Legs trait with leg count
    const newTraitName = `Polypedal Legs (${newLegCount} legs)`
    this.selectedTraitsValue = [...this.selectedTraitsValue, newTraitName]

    // Update the checkbox if it exists
    const checkbox = this.element.querySelector('input[value="Polypedal Legs"]')
    if (checkbox) {
      checkbox.value = newTraitName
      checkbox.checked = true
    }

    // Re-render selected traits to update the UI
    this.updateSelectedTraits()
  }

  updateNaturesBlessingSelections(event) {
    const attributeType = event.target.dataset.attributeType
    const value = event.target.value
    
    if (!this.naturesBlessingSelections) {
      this.naturesBlessingSelections = {}
    }
    
    this.naturesBlessingSelections[attributeType] = value
    
    // If primary changed and it matches secondary, clear secondary
    if (attributeType === 'primary' && this.naturesBlessingSelections.secondary === value) {
      this.naturesBlessingSelections.secondary = ''
    }
    
    // Update hidden field
    this.naturesBlessingSelections = this.normalizeNaturesBlessingSelections(this.naturesBlessingSelections)
    this.syncHiddenState()
    
    // If primary changed, update secondary options dropdown
    if (attributeType === 'primary') {
      // Use setTimeout to ensure DOM is ready
      setTimeout(() => {
        this.updateSecondaryOptions()
      }, 0)
    }
    
    // Re-render selected traits to update the UI
    this.updateSelectedTraits()
  }

  removeTrait(event) {
    const traitName = event.currentTarget.dataset.trait
    const cleanTraitName = traitName.replace('Drawback: ', '')

    // Handle Polypedal Legs variants - find checkbox by base name
    let checkboxValue = cleanTraitName
    if (cleanTraitName.startsWith('Polypedal Legs')) {
      checkboxValue = 'Polypedal Legs'
      // Also remove any Polypedal Legs variant from selected traits
      this.selectedTraitsValue = this.selectedTraitsValue.filter(t => !t.startsWith('Polypedal Legs'))
    }

    const checkbox = this.element.querySelector(`input[value="${checkboxValue}"]`)
    if (checkbox) {
      checkbox.checked = false
      // Only call toggleTrait for non-Polypedal traits (we already handled Polypedal above)
      if (!cleanTraitName.startsWith('Polypedal Legs')) {
        this.toggleTrait({ target: checkbox, preventDefault: () => {} })
      }
    }

    this.updateSelectedTraits()
    this.updateTraitsRemaining()
  }

  updateTraitsRemaining() {
    const heritageData = this.currentHeritageData()

    // Guard against missing heritage data (prevents NaN display)
    if (!heritageData.type) {
      this.traitsRemainingTarget.innerHTML = '<div class="text-gray-500">Select a heritage type above</div>'
      return
    }

    const currentTraits = this.selectedTraitsValue.filter(t => !t.startsWith('Drawback:'))
    const currentDrawbacks = this.selectedTraitsValue.filter(t => t.startsWith('Drawback:'))

    // Calculate max allowed traits based on heritage type
    let maxAllowedTraits = heritageData.maxTraits || heritageData.baseTraits
    if (heritageData.type === 'Green') {
      maxAllowedTraits = heritageData.baseTraits + currentDrawbacks.length
    } else if (heritageData.type === 'Blighted' && currentDrawbacks.length > 0) {
      maxAllowedTraits = heritageData.baseTraits + 1
    }
    // Synthetics use maxTraits (5) directly

    // Display format varies by heritage type
    let displayText = `Selected traits: ${currentTraits.length}/${maxAllowedTraits}`
    if (heritageData.type === 'Synthetic') {
      displayText = `Selected frame modifications: ${currentTraits.length} (any number allowed)`
    }

    this.traitsRemainingTarget.innerHTML = `
      <div>${displayText}</div>
      ${heritageData.drawbacks ? `<div>Selected drawbacks: ${currentDrawbacks.length}</div>` : ''}
    `
  }
} 
